import { Link } from "react-router-dom";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Buildings,
  Envelope,
  CheckCircle,
  Wrench,
  X,
  Tag,

  ArrowLeft,
  Lightbulb,
  Cube,
  User,
  Screwdriver,
  Lightning,
  Drop,
  WashingMachine,
  Bathtub,
  Compass,
  TreePalm,
  DeviceMobile,
  ThermometerCold,
  WifiHigh,
  Snowflake,
  Info,
  CurrencyEur,
  Bed,
  Users,
  Key,
  Elevator,
  Car,
  Plus,
  ClipboardText,
  Question,
  Clock,
  Wall,
} from "@phosphor-icons/react";
import { Avatar, Tooltip } from "@heroui/react";
import DOMPurify from "dompurify";
import {
  TicketLabel,
  TicketInternalStatus,
  TicketPriority,
} from "shared/types/helpdesk";
import { PickerButton } from "../components/pickerButton";
import {
  TenantBalanceBadge,
  tenantBalanceKey,
  type TenantBalance,
} from "shared/components/tenant";
import { getTicketSupplierNames } from "./ticketSuppliers";
import { getUserImageUrlById, getUserName } from "shared/utils/users";
import type { CellConfig } from "shared/components/UniversalTable/components";
import { TABLE_DESIGN } from "shared/components/UniversalTable/constants/tableDesign";
import { COMPLETION_STATUSES, getStatusCategory } from "./statusHelpers";
import { helpdeskPhaseColumns } from "../helpdeskPhaseConfig";

// Helper function to render HTML content safely
const renderHTML = (html: string) => {
  return { __html: DOMPurify.sanitize(html) };
};

// Helper function to check if content contains HTML
const isHTML = (str: string) => {
  return /<[a-z][\s\S]*>/i.test(str);
};

/**
 * Icoon + kleuren voor een ticketlabel.
 *
 * Geëxporteerd zodat andere schermen hetzelfde label-chipje kunnen tonen (o.a. de
 * kolom "Ticketlabel" op de OPEX-facturenpagina) en de kleuren op één plek staan.
 */
export const getLabelIconAndColor = (label: TicketLabel) => {
  const labelConfig: Record<
    TicketLabel,
    { icon: React.ReactNode; bgColor: string; textColor: string }
  > = {
    [TicketLabel.Elektriciteit]: {
      icon: <Lightning className="h-3 w-3" />,
      bgColor: "bg-yellow-100",
      textColor: "text-yellow-800",
    },
    [TicketLabel.VochtWater]: {
      icon: <Drop className="h-3 w-3" />,
      bgColor: "bg-blue-100",
      textColor: "text-blue-800",
    },
    [TicketLabel.Verwarming]: {
      icon: <ThermometerCold className="h-3 w-3" />,
      bgColor: "bg-orange-100",
      textColor: "text-orange-800",
    },
    [TicketLabel.Sanitair]: {
      icon: <Bathtub className="h-3 w-3" />,
      bgColor: "bg-cyan-100",
      textColor: "text-cyan-800",
    },
    [TicketLabel.Onderhoud]: {
      icon: <Wrench className="h-3 w-3" />,
      bgColor: "bg-green-100",
      textColor: "text-green-800",
    },
    [TicketLabel.Poetsen]: {
      icon: <WashingMachine className="h-3 w-3" />,
      bgColor: "bg-pink-100",
      textColor: "text-pink-800",
    },
    [TicketLabel.Internet]: {
      icon: <WifiHigh className="h-3 w-3" />,
      bgColor: "bg-blue-200",
      textColor: "text-blue-900",
    },
    [TicketLabel.Lift]: {
      icon: <Elevator className="h-3 w-3" />,
      bgColor: "bg-indigo-200",
      textColor: "text-indigo-900",
    },
    [TicketLabel.HVAC]: {
      icon: <Snowflake className="h-3 w-3" />,
      bgColor: "bg-sky-100",
      textColor: "text-sky-800",
    },
    [TicketLabel.Verlichting]: {
      icon: <Lightbulb className="h-3 w-3" />,
      bgColor: "bg-yellow-300",
      textColor: "text-yellow-900",
    },
    [TicketLabel.Facturatie]: {
      icon: <CurrencyEur className="h-3 w-3" />,
      bgColor: "bg-emerald-100",
      textColor: "text-emerald-800",
    },
    [TicketLabel.Nutsvoorzieningen]: {
      icon: <Lightning className="h-3 w-3" />,
      bgColor: "bg-purple-100",
      textColor: "text-purple-800",
    },
    [TicketLabel.Boekhouding]: {
      icon: <CurrencyEur className="h-3 w-3" />,
      bgColor: "bg-teal-100",
      textColor: "text-teal-800",
    },
    [TicketLabel.Inrichting]: {
      icon: <Bed className="h-3 w-3" />,
      bgColor: "bg-fuchsia-100",
      textColor: "text-fuchsia-800",
    },
    [TicketLabel.Huurdersdiscussies]: {
      icon: <Users className="h-3 w-3" />,
      bgColor: "bg-rose-100",
      textColor: "text-rose-800",
    },
    [TicketLabel.VragenHuurcontract]: {
      icon: <ClipboardText className="h-3 w-3" />,
      bgColor: "bg-fuchsia-200",
      textColor: "text-fuchsia-900",
    },
    [TicketLabel.Tuinonderhoud]: {
      icon: <TreePalm className="h-3 w-3" />,
      bgColor: "bg-green-200",
      textColor: "text-green-900",
    },
    [TicketLabel.ElectronischeToestellen]: {
      icon: <DeviceMobile className="h-3 w-3" />,
      bgColor: "bg-violet-100",
      textColor: "text-violet-800",
    },
    [TicketLabel.Meubilair]: {
      icon: <Cube className="h-3 w-3" />,
      bgColor: "bg-amber-200",
      textColor: "text-amber-900",
    },
    [TicketLabel.Afbraakwerken]: {
      icon: <Wrench className="h-3 w-3" />,
      bgColor: "bg-gray-100",
      textColor: "text-gray-800",
    },
    [TicketLabel.Asbest]: {
      icon: <X className="h-3 w-3" />,
      bgColor: "bg-red-200",
      textColor: "text-red-900",
    },
    [TicketLabel.Brandbeveiliging]: {
      icon: <Lightning className="h-3 w-3" />,
      bgColor: "bg-red-100",
      textColor: "text-red-800",
    },
    [TicketLabel.Riolering]: {
      icon: <Drop className="h-3 w-3" />,
      bgColor: "bg-blue-300",
      textColor: "text-blue-900",
    },
    [TicketLabel.Ventilatie]: {
      icon: <Compass className="h-3 w-3" />,
      bgColor: "bg-sky-200",
      textColor: "text-sky-900",
    },
    // Default for all other labels
    [TicketLabel.Balustrade]: {
      icon: <Buildings className="h-3 w-3" />,
      bgColor: "bg-indigo-100",
      textColor: "text-indigo-800",
    },
    [TicketLabel.Binnenschrijnwerk]: {
      icon: <Buildings className="h-3 w-3" />,
      bgColor: "bg-amber-100",
      textColor: "text-amber-800",
    },
    [TicketLabel.Dak]: {
      icon: <Buildings className="h-3 w-3" />,
      bgColor: "bg-slate-100",
      textColor: "text-slate-800",
    },
    [TicketLabel.ElastischeVoegen]: {
      icon: <Buildings className="h-3 w-3" />,
      bgColor: "bg-lime-100",
      textColor: "text-lime-800",
    },
    [TicketLabel.Gevel]: {
      icon: <Buildings className="h-3 w-3" />,
      bgColor: "bg-stone-100",
      textColor: "text-stone-800",
    },
    [TicketLabel.Overige]: {
      icon: <Question className="h-3 w-3" />,
      bgColor: "bg-gray-200",
      textColor: "text-gray-900",
    },
    [TicketLabel.Rolluik]: {
      icon: <Buildings className="h-3 w-3" />,
      bgColor: "bg-amber-200",
      textColor: "text-amber-900",
    },
    [TicketLabel.Ruwbouw]: {
      icon: <Buildings className="h-3 w-3" />,
      bgColor: "bg-stone-200",
      textColor: "text-stone-900",
    },
    [TicketLabel.Verstopping]: {
      icon: <Drop className="h-3 w-3" />,
      bgColor: "bg-amber-100",
      textColor: "text-amber-800",
    },
    [TicketLabel.Stabiliteit]: {
      icon: <Buildings className="h-3 w-3" />,
      bgColor: "bg-red-300",
      textColor: "text-red-900",
    },
    [TicketLabel.SuggestiesHuurdersVerbeteringen]: {
      icon: <Lightbulb className="h-3 w-3" />,
      bgColor: "bg-emerald-200",
      textColor: "text-emerald-900",
    },
    [TicketLabel.Trap]: {
      icon: <Buildings className="h-3 w-3" />,
      bgColor: "bg-slate-200",
      textColor: "text-slate-900",
    },
    [TicketLabel.VergunningAttest]: {
      icon: <ClipboardText className="h-3 w-3" />,
      bgColor: "bg-lime-200",
      textColor: "text-lime-900",
    },
    [TicketLabel.VloerenParket]: {
      icon: <Cube className="h-3 w-3" />,
      bgColor: "bg-amber-300",
      textColor: "text-amber-900",
    },
    [TicketLabel.Signalisatie]: {
      icon: <Info className="h-3 w-3" />,
      bgColor: "bg-yellow-100",
      textColor: "text-yellow-800",
    },
    [TicketLabel.Glasbreuk]: {
      icon: <X className="h-3 w-3" />,
      bgColor: "bg-red-100",
      textColor: "text-red-800",
    },
    [TicketLabel.Bezetting]: {
      icon: <Users className="h-3 w-3" />,
      bgColor: "bg-purple-100",
      textColor: "text-purple-800",
    },
    [TicketLabel.Toegangsbeheer]: {
      icon: <Key className="h-3 w-3" />,
      bgColor: "bg-zinc-200",
      textColor: "text-zinc-900",
    },
    [TicketLabel.Buitenschrijnwerk]: {
      icon: <Buildings className="h-3 w-3" />,
      bgColor: "bg-teal-200",
      textColor: "text-teal-900",
    },
    [TicketLabel.Gebouwattributen]: {
      icon: <Car className="h-3 w-3" />,
      bgColor: "bg-amber-200",
      textColor: "text-amber-900",
    },
    [TicketLabel.AfvalEnOngedierte]: {
      icon: <X className="h-3 w-3" />,
      bgColor: "bg-red-200",
      textColor: "text-red-900",
    },
    [TicketLabel.Karweien]: {
      icon: <Wrench className="h-3 w-3" />,
      bgColor: "bg-yellow-200",
      textColor: "text-yellow-900",
    },
    [TicketLabel.Garage]: {
      icon: <Car className="h-3 w-3" />,
      bgColor: "bg-stone-200",
      textColor: "text-stone-900",
    },
    [TicketLabel.UNKNOWN]: {
      icon: <Question className="h-3 w-3" />,
      bgColor: "bg-neutral-secondary-mid/10",
      textColor: "text-neutral-primary",
    },
  };

  return labelConfig[label] || labelConfig[TicketLabel.UNKNOWN];
};

// Helper function to get source abbreviation and color
const getSourceAbbreviationAndColor = (source: string | null) => {
  if (!source) return { abbrev: "", colorClass: "text-neutral-secondary-mid" };
  const lower = source.toLowerCase();
  if (lower.includes("huurder") || lower === "huurder") {
    return { abbrev: "H", colorClass: "text-brick-primary" };
  }
  if (lower.includes("buur") || lower === "buur") {
    return { abbrev: "B", colorClass: "text-brick-primary" };
  }
  if (lower.includes("intern") || lower === "intern") {
    return { abbrev: "Y", colorClass: "text-moss-primary" };
  }
  return {
    abbrev: source.charAt(0).toUpperCase(),
    colorClass: "text-neutral-primary",
  };
};

export interface HelpdeskCellFactoryProps {
  ticket: any;
  context: {
    isSelected: boolean;
    isBulkActive: boolean;
    onToggleSelect: (selected: boolean) => void;
    onClearSelection: () => void;
  };
  handlers: {
    onComplete: (e: React.MouseEvent) => void;
    onMoveToCompletion: (e: React.MouseEvent) => Promise<void>;
    onRevertToOpen: (e: React.MouseEvent) => Promise<void>;
    onSmartInsights: () => void;
    onMerge: () => void;
    onDelete: () => void;
    onCreateInsurance: (e: React.MouseEvent) => void;
    onTitleClick: (e: React.MouseEvent) => void;
  };
  renderPriority: (
    priority: TicketPriority,
    compact: boolean,
  ) => React.ReactNode;
  users: any[];
  /**
   * Openstand per huurder, gesleuteld op `contact:<id>` — één call voor de hele pagina
   * (zie `useTenantBalances`). Ontbreekt een sleutel, dan is er geen openstand en toont
   * het badge niets.
   */
  tenantBalances?: Record<string, TenantBalance>;
}

/**
 * Factory function to create cell configurations for helpdesk ticket rows
 * This keeps the UniversalTableRow component agnostic of helpdesk-specific logic
 */
export const createHelpdeskCells = ({
  ticket,
  context,
  handlers,
  renderPriority,
  users,
  tenantBalances,
}: HelpdeskCellFactoryProps): CellConfig[] => {
  return [
    // Checkbox Column
    {
      id: "checkbox",
      colSpan: 1,
      align: "center",
      content: (
        <Checkbox
          checked={context.isSelected}
          onCheckedChange={(checked) => {
            context.onToggleSelect(checked === true);
          }}
          onClick={(e) => e.stopPropagation()}
        />
      ),
    },
    // Actions Column
    {
      id: "actions",
      colSpan: 1,
      align: "center",
      content: (
        <div className="flex items-center justify-center gap-1">
          {getStatusCategory(ticket.internalStatus) !== "closed" && (
            <>
              {handlers.onRevertToOpen &&
                COMPLETION_STATUSES.includes(ticket.internalStatus) && (
                  <button
                    onClick={handlers.onRevertToOpen}
                    className="shrink-0 rounded-md bg-yellow-100 p-1 text-xs text-yellow-600 shadow-sm transition-all duration-200 ease-in-out hover:scale-110 hover:bg-yellow-600 hover:text-white hover:shadow-md focus:ring-2 focus:ring-yellow-300 active:scale-95"
                    title="Terug naar openstaande tickets"
                  >
                    <ArrowLeft className="h-3 w-3 transition-transform duration-200" />
                  </button>
                )}
              <button
                onClick={handlers.onComplete}
                className="shrink-0 rounded-md bg-green-100 p-1 text-xs text-green-600 shadow-sm transition-all duration-200 ease-in-out hover:scale-110 hover:bg-green-600 hover:text-white hover:shadow-md focus:ring-2 focus:ring-green-300 active:scale-95"
                title="Voltooi ticket"
              >
                <CheckCircle className="h-3 w-3 transition-transform duration-200" />
              </button>
            </>
          )}

          {/* Merge and Delete buttons */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              handlers.onMerge();
            }}
            className="shrink-0 rounded-md bg-gray-100 p-1 text-xs text-gray-600 shadow-sm transition-all duration-200 ease-in-out hover:scale-110 hover:bg-blue-600 hover:text-white hover:shadow-md focus:ring-2 focus:ring-blue-300 active:scale-95"
            title="Tickets samenvoegen"
          >
            <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handlers.onDelete();
            }}
            className="shrink-0 rounded-md bg-gray-100 p-1 text-xs text-gray-600 shadow-sm transition-all duration-200 ease-in-out hover:scale-110 hover:bg-red-600 hover:text-white hover:shadow-md focus:ring-2 focus:ring-red-300 active:scale-95"
            title="Verwijder ticket"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      ),
    },
    // Communication Column
    {
      id: "communication",
      colSpan: 1,
      align: "center",
      content: (
        <div className="flex items-center justify-center gap-1">
          {/* Customer Email Button */}
          <div className="relative">
            <button
              onClick={(e) => {
                e.stopPropagation();
                window.location.href = `/helpdesk/${ticket.id}?mode=tenant`;
              }}
              className={`relative flex items-center gap-1 rounded-md px-1.5 py-1 text-[11px] font-medium shadow-sm transition-all duration-200 ease-in-out hover:scale-105 hover:shadow-md focus:ring-2 active:scale-95 ${
                (ticket as any).hasTenantUnreadEmails
                  ? "bg-red-100 text-red-600 ring-1 ring-red-200 hover:bg-red-600 hover:text-white focus:ring-red-300"
                  : "bg-blue-100 text-blue-600 hover:bg-blue-600 hover:text-white focus:ring-blue-300"
              }`}
              title={`Communicatie met huurder${(ticket as any).tenantEmailCount ? ` (${(ticket as any).tenantEmailCount} emails)` : " (0 emails)"}${(ticket as any).hasTenantUnreadEmails ? ` - ${(ticket as any).tenantUnreadEmailCount} ongelezen` : ""}`}
            >
              <Envelope className="h-3 w-3" />
              <span className="text-[11px] font-semibold">
                {(ticket as any).tenantEmailCount || 0}
              </span>
              {(ticket as any).hasTenantUnreadEmails && (
                <div className="absolute -top-1 -right-1">
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75"></span>
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-red-600"></span>
                  </span>
                </div>
              )}
            </button>
          </div>

          {/* Supplier Communication Button */}
          <div className="relative">
            <button
              onClick={(e) => {
                e.stopPropagation();
                window.location.href = `/helpdesk/${ticket.id}?mode=supplier`;
              }}
              className={`relative flex items-center gap-1 rounded-md px-1.5 py-1 text-[11px] font-medium shadow-sm transition-all duration-200 ease-in-out hover:scale-105 hover:shadow-md focus:ring-2 active:scale-95 ${
                (ticket as any).hasSupplierUnreadEmails
                  ? "bg-red-100 text-red-600 ring-1 ring-red-200 hover:bg-red-600 hover:text-white focus:ring-red-300"
                  : (ticket as any).supplierEmailCount > 0
                    ? "bg-neutral-primary/10 hover:bg-neutral-primary text-neutral-primary focus:ring-neutral-primary/20 hover:text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-600 hover:text-white focus:ring-gray-300"
              }`}
              title={`Leverancierscommunicatie${(ticket as any).supplierEmailCount ? ` (${(ticket as any).supplierEmailCount} emails)` : " (0 emails)"}${(ticket as any).hasSupplierUnreadEmails ? ` - ${(ticket as any).supplierUnreadEmailCount} ongelezen` : ""}`}
            >
              <Wrench className="h-3 w-3" />
              <span className="text-[11px] font-semibold">
                {(ticket as any).supplierEmailCount || 0}
              </span>
              {(ticket as any).hasSupplierUnreadEmails && (
                <div className="absolute -top-1 -right-1">
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75"></span>
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-red-600"></span>
                  </span>
                </div>
              )}
            </button>
          </div>

        </div>
      ),
    },
    // Priority Column
    {
      id: "priority",
      colSpan: 1,
      align: "center",
      content: renderPriority(ticket.priority, true),
    },
    // Insurance Column
    {
      id: "insurance",
      colSpan: 1,
      align: "center",
      content: ticket.insuranceIssue?.id ? (
        <a
          href={`/insurance/detail/${ticket.insuranceIssue.id}`}
          className={`${TABLE_DESIGN.cellContent.badge.base} ${TABLE_DESIGN.cellContent.badge.interactive} ${TABLE_DESIGN.cellContent.badge.compact} bg-moss-primary/10 text-moss-primary hover:bg-moss-primary hover:text-white`}
          onClick={(e) => e.stopPropagation()}
          title="Open verzekeringsdossier"
        >
          <span className="font-semibold">INS{ticket.insuranceIssue.id}</span>
        </a>
      ) : (
        <button
          onClick={handlers.onCreateInsurance}
          className={`${TABLE_DESIGN.cellContent.button.secondary} ${TABLE_DESIGN.cellContent.button.compact} ${TABLE_DESIGN.cellContent.button.interactive}`}
          title="Maak verzekeringsdossier"
        >
          <Plus className={TABLE_DESIGN.cellContent.icon.small} />
        </button>
      ),
      onClick: (e) => e.stopPropagation(),
    },
    // Ticket & Label Column
    {
      id: "ticket-label",
      colSpan: 1,
      align: "left",
      content: (
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex min-w-0 items-center gap-2">
            <span className={TABLE_DESIGN.cellContent.text.id}>
              #{ticket.id}
            </span>
            <Tooltip
              content={
                <div className="max-h-64 max-w-md overflow-y-auto p-3">
                  <div className="text-neutral-primary mb-2 border-b border-gray-200 pb-2 text-base font-semibold break-words whitespace-pre-wrap">
                    {ticket.title}
                  </div>
                  <div className="text-neutral-primary mb-1 text-xs font-medium tracking-wide uppercase">
                    Beschrijving
                  </div>
                  <div className="text-sm text-gray-600">
                    {ticket.description ? (
                      isHTML(ticket.description) ? (
                        <div
                          className="break-words whitespace-pre-wrap"
                          dangerouslySetInnerHTML={renderHTML(
                            ticket.description,
                          )}
                        />
                      ) : (
                        <div className="break-words whitespace-pre-wrap">
                          {ticket.description.length > 300
                            ? `${ticket.description.substring(0, 300)}...`
                            : ticket.description}
                        </div>
                      )
                    ) : (
                      "Geen beschrijving beschikbaar"
                    )}
                  </div>
                </div>
              }
              placement="top"
              delay={500}
            >
              <button
                onClick={handlers.onTitleClick}
                className={`${TABLE_DESIGN.cellContent.text.title} ${ticket.internalStatus === TicketInternalStatus.NEW_UNANSWERED ? TABLE_DESIGN.cellContent.text.titleUnread : ""} min-w-0 flex-1 truncate text-left`}
                title="Klik om email geschiedenis te bekijken"
              >
                {ticket.title}
              </button>
            </Tooltip>
            {/* Label modal trigger */}
            <div className="shrink-0">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const ev = new CustomEvent("helpdesk-open-picker", {
                    detail: {
                      mode: "label",
                      ticketId: ticket.id,
                      bulkSelection: context.isBulkActive,
                    },
                  });
                  window.dispatchEvent(ev);
                }}
                className={`${TABLE_DESIGN.cellContent.badge.base} ${TABLE_DESIGN.cellContent.badge.interactive} ${TABLE_DESIGN.cellContent.badge.compact} whitespace-nowrap ${
                  ticket.label && ticket.label !== TicketLabel.UNKNOWN
                    ? `${getLabelIconAndColor(ticket.label).bgColor} ${getLabelIconAndColor(ticket.label).textColor}`
                    : "bg-brick-primary text-white"
                }`}
              >
                {ticket.label && ticket.label !== TicketLabel.UNKNOWN ? (
                  <>
                    {getLabelIconAndColor(ticket.label).icon}
                    <span className="whitespace-nowrap">{ticket.label}</span>
                  </>
                ) : (
                  <Tag className="h-4 w-4 transition-transform duration-200" />
                )}
              </button>
            </div>
            {/* Renovation project badge */}
            {ticket.renovationProject && (
              <div className="shrink-0">
                <Tooltip
                  content={`Renovatieproject: ${ticket.renovationProject.title}`}
                  placement="top"
                  delay={300}
                >
                  <Link
                    to={`/renovation/${ticket.renovationProject.id}`}
                    onClick={(e) => e.stopPropagation()}
                    className={`${TABLE_DESIGN.cellContent.badge.base} ${TABLE_DESIGN.cellContent.badge.compact} inline-flex items-center gap-1 whitespace-nowrap bg-amber-100 text-amber-800 hover:bg-amber-200`}
                  >
                    <Wall className="h-3 w-3 shrink-0" />
                    <span className="max-w-[100px] truncate text-xs">
                      {ticket.renovationProject.title}
                    </span>
                  </Link>
                </Tooltip>
              </div>
            )}
          </div>
        </div>
      ),
    },
    // Building Column
    {
      id: "building",
      colSpan: 1,
      align: "center",
      hidden: true,
      content: (
        <PickerButton
          mode="building"
          ticketId={ticket.id}
          value={ticket.building?.id || null}
          icon={<Buildings className="text-neutral-secondary-mid h-3 w-3" />}
          selectedTicketIds={[]}
          onClearSelection={context.onClearSelection}
        />
      ),
    },
    // Unit Column
    {
      id: "unit",
      colSpan: 1,
      align: "center",
      hidden: true,
      content: (
        <PickerButton
          mode="unit"
          ticketId={ticket.id}
          value={ticket.unit?.id || null}
          icon={<Cube className="text-neutral-secondary-mid h-3 w-3" />}
          selectedTicketIds={[]}
          onClearSelection={context.onClearSelection}
        />
      ),
    },
    // Source Column
    {
      id: "source",
      colSpan: 1,
      align: "center",
      className: "hidden xl:flex",
      content: (
        <button
          onClick={(e) => {
            e.stopPropagation();
            const ev = new CustomEvent("helpdesk-open-picker", {
              detail: {
                mode: "source",
                ticketId: ticket.id,
                bulkSelection: context.isBulkActive,
              },
            });
            window.dispatchEvent(ev);
          }}
          className="px-1 py-0.5"
          title={`Bron: ${ticket.source || "Niet ingesteld"} - Klik om te wijzigen`}
        >
          {(() => {
            const s = getSourceAbbreviationAndColor(ticket.source);
            return (
              <span className={`text-[11px] font-bold ${s.colorClass}`}>
                {s.abbrev}
              </span>
            );
          })()}
        </button>
      ),
    },
    // Reporter Column
    {
      id: "reporter",
      colSpan: 1,
      align: "center",
      hidden: true,
      content: (
        <div className="flex min-w-0 items-center gap-1">
          <span className="min-w-0 flex-1">
            <PickerButton
              mode="reporter"
              ticketId={ticket.id}
              value={
                (ticket.reporter as any)?.firstName
                  ? `${(ticket.reporter as any).firstName} ${(ticket.reporter as any).lastName}`
                  : null
              }
              icon={<User className="text-neutral-secondary-mid h-3 w-3" />}
              classNames="extra-bold"
              textClassName="extra-bold"
              selectedTicketIds={[]}
              onClearSelection={context.onClearSelection}
            />
          </span>
          {/* Openstand van de melder, naast zijn naam. Het badge staat buiten de knop:
              het is zelf een link naar het incassodossier, en een link in een knop is
              ongeldige HTML. Niets zichtbaar = geen openstand. */}
          <TenantBalanceBadge
            name={
              (ticket.reporter as any)?.firstName
                ? `${(ticket.reporter as any).firstName} ${(ticket.reporter as any).lastName}`
                : undefined
            }
            balance={
              tenantBalances?.[
                tenantBalanceKey("contact", (ticket.reporter as any)?.id) ?? ""
              ]
            }
          />
        </div>
      ),
    },
    // Supplier Column
    {
      id: "supplier",
      colSpan: 1,
      align: "center",
      hidden: true,
      content: (
        <PickerButton
          mode="supplier"
          ticketId={ticket.id}
          value={getTicketSupplierNames(ticket) || null}
          icon={<Screwdriver className="text-neutral-secondary-mid h-3 w-3" />}
          selectedTicketIds={[]}
          onClearSelection={context.onClearSelection}
        />
      ),
    },
    // Assignee Column
    {
      id: "assignee",
      colSpan: 1,
      align: "center",
      content: (
        <PickerButton
          mode="assignee"
          ticketId={ticket.id}
          value={
            (ticket as any).assignedTo
              ? getUserName(ticket.assignedTo, users).name
              : null
          }
          icon={
            <Avatar
              className="h-4 w-4"
              src={getUserImageUrlById(ticket.assignedTo, users)}
            />
          }
          selectedTicketIds={[]}
          onClearSelection={context.onClearSelection}
        />
      ),
    },
    // Internal Status Column
    {
      id: "internal-status",
      colSpan: 1,
      align: "left",
      content: (() => {
        const phaseConfig = helpdeskPhaseColumns.find(
          (col) => col.id === ticket.internalStatus,
        );
        const label = phaseConfig?.label || ticket.internalStatus || "-";
        const colorClasses = phaseConfig?.color || "text-gray-700";
        const bgColorClasses = phaseConfig?.bgColor || "bg-gray-50";

        return (
          <button
            onClick={(e) => {
              e.stopPropagation();
              const ev = new CustomEvent("helpdesk-open-picker", {
                detail: {
                  mode: "internal-status",
                  ticketId: ticket.id,
                  bulkSelection: context.isBulkActive,
                },
              });
              window.dispatchEvent(ev);
            }}
            // De kolom is smal (plaats gemaakt voor "Ticket & Label") en de fasenamen
            // zijn lang, dus kapt de pill af met "…" i.p.v. hard weggeknipt te worden
            // door de `overflow-hidden` van de cel. De volledige fase staat in de
            // tooltip, zodat er niets onleesbaar wordt.
            title={label}
            className={`flex min-w-0 max-w-full items-center rounded-md px-2 py-1 ${bgColorClasses} whitespace-nowrap hover:opacity-80 transition-opacity cursor-pointer`}
          >
            <span className={`truncate text-xs font-medium ${colorClasses}`}>
              {label}
            </span>
          </button>
        );
      })(),
    },
    // Days in Phase Column
    {
      id: "days-in-phase",
      colSpan: 1,
      align: "center",
      content: (
        <div className="flex items-center justify-center gap-1">
          {ticket.daysInCurrentPhase !== undefined &&
          ticket.daysInCurrentPhase >= 0 ? (
            <>
              <Clock
                weight="duotone"
                size={14}
                className={
                  ticket.daysInCurrentPhase > 7
                    ? "text-red-500"
                    : ticket.daysInCurrentPhase > 3
                      ? "text-orange-500"
                      : "text-gray-500"
                }
              />
              <span
                className={`text-xs font-medium ${
                  ticket.daysInCurrentPhase > 7
                    ? "text-red-700"
                    : ticket.daysInCurrentPhase > 3
                      ? "text-orange-700"
                      : "text-gray-600"
                }`}
              >
                {ticket.daysInCurrentPhase}d
              </span>
            </>
          ) : (
            <span className="text-xs text-gray-400">-</span>
          )}
        </div>
      ),
    },
    // Interventions Column — de geplande interventiedatum zelf. Een teller met
    // agenda-icoon zei alleen DAT er iets gepland stond; de datum is waar de
    // helpdesk op werkt. Oranje wanneer die datum voorbij is; bij meerdere open
    // interventies staat de vroegste datum met een "+N" erachter.
    {
      id: "interventions",
      colSpan: 1,
      align: "center",
      content: (() => {
        const count = (ticket as any).interventionCount || 0;
        const overdue = !!(ticket as any).hasOverdueIntervention;
        const nextDate = (ticket as any).nextInterventionDate;

        if (!nextDate) {
          return <span className="text-xs text-gray-300">—</span>;
        }

        const plannedDate = new Date(nextDate);
        return (
          <Link
            to={`/helpdesk/${ticket.id}`}
            onClick={(e) => e.stopPropagation()}
            className={`flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-semibold whitespace-nowrap transition-colors ${
              overdue
                ? "bg-orange-100 text-orange-700 ring-1 ring-orange-300 hover:bg-orange-200"
                : "bg-blue-100 text-blue-700 hover:bg-blue-200"
            }`}
            title={`${count} geplande interventie(s) — eerstvolgende op ${plannedDate.toLocaleDateString("nl-BE")}${overdue ? " (datum is voorbij)" : ""}`}
          >
            <span>
              {plannedDate.toLocaleDateString("nl-BE", {
                day: "2-digit",
                month: "2-digit",
                year: "2-digit",
              })}
            </span>
            {count > 1 && (
              <span className="font-normal opacity-70">+{count - 1}</span>
            )}
          </Link>
        );
      })(),
    },
    // Invoice Total Column — som van de gekoppelde OPEX-factuurregels. Linkt naar
    // de facturensectie van het ticket, zodat je van bedrag naar factuur kan.
    {
      id: "invoice-total",
      colSpan: 1,
      align: "center",
      hidden: true,
      content: (() => {
        const total = Number((ticket as any).actualCost ?? 0);
        const invoiceNumbers: string[] = (ticket as any).invoiceNumbers ?? [];

        if (!total) {
          return <span className="text-xs text-gray-300">—</span>;
        }

        return (
          <Link
            to={`/helpdesk/${ticket.id}?section=invoices`}
            onClick={(e) => e.stopPropagation()}
            className="text-moss-primary hover:text-moss-primaryDark text-xs font-semibold whitespace-nowrap transition-colors hover:underline"
            title={
              invoiceNumbers.length
                ? `Facturen: ${invoiceNumbers.join(", ")}`
                : "Bekijk gekoppelde facturen"
            }
          >
            {new Intl.NumberFormat("nl-BE", {
              style: "currency",
              currency: "EUR",
              maximumFractionDigits: 0,
            }).format(total)}
          </Link>
        );
      })(),
    },
    // Date Column — aanmaakdatum, kort genoteerd: minder belangrijk dan de rest,
    // dus zo smal mogelijk gehouden.
    {
      id: "date",
      colSpan: 1,
      align: "center",
      hidden: true,
      content: (
        <div
          className="text-neutral-secondary-mid text-[11px] whitespace-nowrap"
          title={new Date(ticket.createdAt).toLocaleDateString("nl-BE")}
        >
          {new Date(ticket.createdAt).toLocaleDateString("nl-BE", {
            day: "2-digit",
            month: "2-digit",
            year: "2-digit",
          })}
        </div>
      ),
    },
  ];
};
